import React from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { timestampsState } from 'recoils/common/menuState';
import styled, { css } from 'styled-components';

import Tag from 'components/common/Tag';

import {
  arrowTransition,
  text,
  textHoverTransition,
} from 'styles/commons/text';
import theme from 'styles/token/color';
import { typography } from 'styles/token/typography';
import { getMenuTagProps } from 'utils/date/convertDateUtil';

/**
 * 내/외부 링크를 판단하여 적절한 컴포넌트(text.HoverLink)를 리턴하는 헬퍼 컴포넌트
 */
const SmartLink = React.forwardRef(({ path, children, ...props }, ref) => {
  const isInternal = path?.startsWith('/');

  if (isInternal) {
    return (
      <text.HoverLink ref={ref} to={path} {...props}>
        {children}
      </text.HoverLink>
    );
  }

  return (
    <text.HoverLink
      ref={ref}
      as="a"
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </text.HoverLink>
  );
});

/**
 * @param {*} item 메뉴의 아이템 정보
 * @param {*} openDepthIndex 현재 활성화된 메뉴 인덱스 (hover/active 관리)
 * @param {*} index 메뉴 인덱스
 * @param {*} icon 아이콘 표출 판단
 * @returns
 */
const RightSubMenu = ({
  item,
  openDepthIndex,
  index,
  icon = false,
  onMenuBarClose,
}) => {
  const timestamps = useRecoilValue(timestampsState);
  const isActive = openDepthIndex === index;

  const getTagItem = (targetItem) => {
    if (timestamps?.[targetItem.tagType]) return timestamps[targetItem.tagType];
    if (targetItem.createdAt || targetItem.updatedAt) return targetItem;
    if (targetItem.content?.length > 0) {
      for (const child of targetItem.content) {
        if (getMenuTagProps(child).isRecent) return child;
      }
    }
    return { createdAt: targetItem.createdAt, updatedAt: targetItem.updatedAt };
  };

  return (
    <RightSubMenuWapper>
      {/* 단독 아이템 */}
      {!item.verticals && (
        <RightBlock>
          <RightBlockLink
            path={item.path}
            target={item.isNewTab ? '_blank' : undefined}
            className="focus"
            onClick={onMenuBarClose}
          >
            <RightSubMenuTitle className={isActive ? 'active' : ''}>
              {item.title}
              {item.isNewTab && <FiArrowUpRight className="arrow-top" />}
              <Tag.Menu item={getTagItem(item)} />
            </RightSubMenuTitle>
            <RightSubMenuDesc>{item.description}</RightSubMenuDesc>
          </RightBlockLink>
        </RightBlock>
      )}

      {/* 추가: 세로 정렬 리스트 (ex: MCP 서버 하위 메뉴) */}
      {item.verticals?.length > 0 &&
        item.verticals.map((data, idx) => (
          <RightBlock key={`vertical-${idx}`}>
            <RightBlockLink
              path={data.path}
              target={data.isNewTab ? '_blank' : undefined}
              className="focus"
              onClick={onMenuBarClose}
            >
              <RightSubMenuTitle>
                {data.title}
                {data.isNewTab && <FiArrowUpRight className="arrow-top" />}
                <Tag.Menu item={getTagItem(data)} />
              </RightSubMenuTitle>
              <RightSubMenuDesc>{data.description}</RightSubMenuDesc>
            </RightBlockLink>
          </RightBlock>
        ))}

      {/* 하위 리스트 아이템 : ex) api */}
      {item.apis?.length > 0 && (
        <RightBlockGrid id="grid">
          {item.apis?.map((data, idx) => {
            return (
              <RightBlockItem
                key={`right-menu-list${idx}`}
                as={
                  data.path && !data.content && !data.apis ? SmartLink : 'div'
                }
                path={
                  data.path && !data.content && !data.apis
                    ? data.path
                    : undefined
                }
                className={data.content === undefined ? 'focus' : ''}
                /* path가 존재해서 링크 구조일 때만 클릭 시 닫힘 */
                onClick={
                  data.path && !data.content && !data.apis
                    ? onMenuBarClose
                    : undefined
                }
              >
                <RightSubMenuTitle>
                  {data.path && (data.content || data.apis) ? (
                    <SmartLink
                      path={data.path}
                      onClick={onMenuBarClose}
                      target={data.isNewTab ? '_blank' : undefined}
                    >
                      {data.title}
                      {data.isNewTab && (
                        <FiArrowUpRight className="arrow-top" />
                      )}
                    </SmartLink>
                  ) : (
                    <>
                      {data.title}
                      {data.isNewTab && (
                        <FiArrowUpRight className="arrow-top" />
                      )}
                    </>
                  )}
                </RightSubMenuTitle>
                <RightSubMenuDesc>{data.description}</RightSubMenuDesc>
                {data.content && (
                  <RightBlockList className="block">
                    {data.content?.map((link, i) => (
                      <RightBlockChildLink
                        path={link.path}
                        key={`right-item-${i}`}
                        onClick={onMenuBarClose}
                        target={link.isNewTab ? '_blank' : undefined}
                      >
                        {link.title}
                        {link.isNewTab && (
                          <FiArrowUpRight className="arrow-top" />
                        )}
                        <Tag.Menu item={link} />
                        {icon && link.type !== 'link' && icon}
                      </RightBlockChildLink>
                    ))}
                  </RightBlockList>
                )}
              </RightBlockItem>
            );
          })}
        </RightBlockGrid>
      )}
    </RightSubMenuWapper>
  );
};

export default RightSubMenu;
export { arrowTransition, textHoverTransition };

const RightSubMenuWapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;

  .arrow-top {
    font-size: 1em;
    margin-left: 0px;
  }

  .small {
    margin-left: 6px;
  }
`;

const RightBlockChildLink = styled(SmartLink)`
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;

  color: ${theme.color.gray100};
  ${typography.bodyExtraSmall}
  line-height: 18px;
  padding-right: 32px;

  &:hover {
    color: ${theme.color.primary};

    .arrow {
      stroke: ${theme.color.primary};
    }
  }
  ${arrowTransition}
`;

const RightSubMenuTitle = styled.p`
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${theme.color.white};
  ${typography.bodyMedium};

  a {
    color: inherit;
    text-decoration: none;
    transition: all 0.3s ease-in-out;
    display: inline-flex;
    align-items: center;
    gap: 2px;

    &:hover {
      color: ${theme.color.primary};
    }
  }
`;

const RightSubMenuDesc = styled.span`
  display: inline-block;
  width: 100%;
  /* padding-top: 8px; */
  color: ${theme.color.gray400};
  ${typography.bodyExtraSmall}
  word-break: break-all;
  white-space: pre-line;
`;

const RightBlock = styled.div``;

const RightBlockGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  border-top: 1px solid ${theme.color.gray800};
  padding-top: 32px;
`;

const RightBlockInlineGrid = styled.div`
  display: flex;
  /* gap: 16px; */
  margin-top: 12px;

  ${RightBlockChildLink} {
    &:hover {
      color: ${theme.color.primary};
    }
  }
`;

const RightBlockList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const RightBlockItem = styled.div`
  &.focus {
    ${textHoverTransition}
  }
`;

const RightBlockLink = styled(SmartLink)`
  transition: all 0.3s ease-in-out;
  &:hover {
    cursor: ${({ path }) => (path ? 'pointer' : 'default')};
  }

  &.focus {
    ${textHoverTransition}
  }
`;
